import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { reviewClassificationMarkings, uploadAndReviewClassification } from '../services/api';
import FileUpload from '../components/FileUpload';
import ClassificationBadge from '../components/ClassificationBadge';
import SecurityShieldLoader from '../components/SecretSquirrelLoader';
import SCGCitation from '../components/SCGCitation';
import {
  DocumentMagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';

// Helper function to linkify SCG rule IDs in text
const linkifyScgRules = (text) => {
  if (!text) return null;

  // Pattern to match SCG rule IDs like SCG-XXX-XXXXXX-XXX
  const scgPattern = /(SCG-[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+)/g;
  const parts = text.split(scgPattern);

  return parts.map((part, index) => {
    if (scgPattern.test(part)) {
      // Reset lastIndex since we're reusing the regex
      scgPattern.lastIndex = 0;
      return <SCGCitation key={index} ruleId={part} compact={true} />;
    }
    return <span key={index}>{part}</span>;
  });
};

// Severity badge component
const SeverityBadge = ({ severity }) => {
  const severityStyles = {
    critical: 'bg-red-900/30 text-red-400 border-red-700',
    high: 'bg-orange-900/30 text-orange-400 border-orange-700',
    medium: 'bg-yellow-900/30 text-yellow-400 border-yellow-700',
    low: 'bg-blue-900/30 text-blue-400 border-blue-700',
    none: 'bg-green-900/30 text-green-400 border-green-700'
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${severityStyles[severity] || severityStyles.none}`}>
      {severity?.toUpperCase() || 'NONE'}
    </span>
  );
};

// Discrepancy type indicator
const DiscrepancyIndicator = ({ type }) => {
  if (type === 'UNDER_CLASSIFIED') {
    return (
      <div className="flex items-center text-red-400">
        <ArrowUpIcon className="w-4 h-4 mr-1" />
        <span className="text-sm font-medium">Under-Classified</span>
      </div>
    );
  } else if (type === 'OVER_CLASSIFIED') {
    return (
      <div className="flex items-center text-yellow-400">
        <ArrowDownIcon className="w-4 h-4 mr-1" />
        <span className="text-sm font-medium">Over-Classified</span>
      </div>
    );
  }
  return null;
};

// Statistics card component
const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
  const colorStyles = {
    blue: 'bg-blue-900/20 text-blue-400 border-blue-800',
    green: 'bg-green-900/20 text-green-400 border-green-800',
    red: 'bg-red-900/20 text-red-400 border-red-800',
    yellow: 'bg-yellow-900/20 text-yellow-400 border-yellow-800',
    orange: 'bg-orange-900/20 text-orange-400 border-orange-800'
  };

  return (
    <div className={`p-4 rounded-lg border ${colorStyles[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </div>
  );
};

// Portion review card component
const PortionReviewCard = ({ review, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  const [textExpanded, setTextExpanded] = useState(false);
  const hasDiscrepancy = review.comparison?.has_discrepancy;
  const ANALYSIS_TRUNCATE_LENGTH = 300;
  const TEXT_TRUNCATE_LENGTH = 500;

  return (
    <div className={`border rounded-lg overflow-hidden ${hasDiscrepancy ? 'border-red-800 bg-red-900/10' : 'border-green-800 bg-green-900/10'}`}>
      {/* Header */}
      <div
        className={`p-4 cursor-pointer flex items-center justify-between ${hasDiscrepancy ? 'bg-red-900/20' : 'bg-green-900/20'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center space-x-4">
          <span className="font-mono text-sm bg-[var(--vault-elevated)] px-2 py-1 rounded">
            Portion {review.portion_index + 1}
          </span>
          <span className="font-mono text-sm font-medium">
            {review.original_marking || '(no marking)'}
          </span>
          {hasDiscrepancy ? (
            <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
          ) : (
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
          )}
        </div>
        <div className="flex items-center space-x-3">
          {hasDiscrepancy && <DiscrepancyIndicator type={review.comparison?.discrepancy_type} />}
          <SeverityBadge severity={review.comparison?.severity} />
          <span className="text-[var(--text-muted)] text-sm">
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="p-4 space-y-4 border-t border-[var(--vault-border)]">
          {/* Text preview - expandable */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-1">Portion Text</h4>
            <div className="text-sm text-[var(--text-muted)] bg-[var(--vault-elevated)] p-3 rounded border border-[var(--vault-border)]">
              {review.original_text?.length > TEXT_TRUNCATE_LENGTH && !textExpanded ? (
                <>
                  <p className="whitespace-pre-wrap">{review.original_text.substring(0, TEXT_TRUNCATE_LENGTH)}...</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setTextExpanded(true); }}
                    className="mt-2 text-[var(--spotlight-gold)] hover:text-[var(--spotlight-gold)] text-xs font-medium"
                  >
                    Show full text ▼
                  </button>
                </>
              ) : (
                <>
                  <p className="whitespace-pre-wrap">{review.original_text}</p>
                  {review.original_text?.length > TEXT_TRUNCATE_LENGTH && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setTextExpanded(false); }}
                      className="mt-2 text-[var(--spotlight-gold)] hover:text-[var(--spotlight-gold)] text-xs font-medium"
                    >
                      Show less ▲
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Classification comparison - always show full classification output */}
          <div className="grid grid-cols-2 gap-4">
            <div className={`bg-[var(--vault-elevated)] p-3 rounded border ${hasDiscrepancy ? 'border-red-800' : 'border-green-800'}`}>
              <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Current Classification</h4>
              <ClassificationBadge classification={review.existing_classification?.classification || 'UNKNOWN'} />
              <p className="text-xs font-mono text-[var(--text-secondary)] mt-2 bg-[var(--vault-bg)] p-2 rounded">
                {review.existing_classification?.full_classification || review.original_marking || 'UNKNOWN'}
              </p>
              {review.existing_classification?.caveats?.length > 0 && (
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="font-medium">Caveats:</span> {review.existing_classification.caveats.join(', ')}
                </p>
              )}
              {review.existing_classification?.dissemination_controls?.length > 0 && (
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="font-medium">Controls:</span> {review.existing_classification.dissemination_controls.join(', ')}
                </p>
              )}
            </div>
            <div className={`bg-[var(--vault-elevated)] p-3 rounded border ${hasDiscrepancy ? 'border-blue-800' : 'border-green-800'}`}>
              <h4 className={`text-sm font-medium mb-2 ${hasDiscrepancy ? 'text-[var(--spotlight-gold)]' : 'text-green-400'}`}>
                {hasDiscrepancy ? 'Suggested Classification' : 'Verified Classification'}
              </h4>
              <ClassificationBadge classification={review.suggested_classification?.classification || 'UNKNOWN'} />
              <p className="text-xs font-mono text-[var(--text-secondary)] mt-2 bg-[var(--vault-bg)] p-2 rounded">
                {review.suggested_classification?.full_classification || review.suggested_classification?.classification || 'UNKNOWN'}
              </p>
              {review.suggested_classification?.caveats?.length > 0 && (
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="font-medium">Caveats:</span> {review.suggested_classification.caveats.join(', ')}
                </p>
              )}
              {review.suggested_classification?.dissemination_controls?.length > 0 && (
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="font-medium">Controls:</span> {review.suggested_classification.dissemination_controls.join(', ')}
                </p>
              )}
              <p className="text-xs text-[var(--text-muted)] mt-1">
                <span className="font-medium">Confidence:</span> {((review.confidence || 0) * 100).toFixed(0)}%
              </p>
            </div>
          </div>

          {/* Comparison details */}
          {hasDiscrepancy && review.comparison && (
            <div className="bg-[var(--vault-elevated)] p-3 rounded border border-[var(--vault-border)]">
              <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Discrepancy Details</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {review.comparison.missing_caveats?.length > 0 && (
                  <div className="text-red-400">
                    <span className="font-medium">Missing Caveats:</span> {review.comparison.missing_caveats.join(', ')}
                  </div>
                )}
                {review.comparison.extra_caveats?.length > 0 && (
                  <div className="text-yellow-400">
                    <span className="font-medium">Extra Caveats:</span> {review.comparison.extra_caveats.join(', ')}
                  </div>
                )}
                {review.comparison.missing_dissemination?.length > 0 && (
                  <div className="text-red-400">
                    <span className="font-medium">Missing Controls:</span> {review.comparison.missing_dissemination.join(', ')}
                  </div>
                )}
                {review.comparison.extra_dissemination?.length > 0 && (
                  <div className="text-yellow-400">
                    <span className="font-medium">Extra Controls:</span> {review.comparison.extra_dissemination.join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SCG Citations - with linkification */}
          {review.scg_citations?.length > 0 && (
            <div className="bg-[var(--vault-elevated)] p-3 rounded border border-[var(--vault-border)]">
              <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">SCG Citations</h4>
              <div className="space-y-3">
                {review.scg_citations.map((citation, i) => (
                  <div key={i} className="flex items-start space-x-3 border-l-2 border-[var(--spotlight-gold)] pl-3">
                    <div className="flex-shrink-0">
                      <SCGCitation ruleId={citation.rule_id} compact={true} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <ClassificationBadge classification={citation.classification_level || 'UNKNOWN'} />
                        {citation.similarity && (
                          <span className="text-xs text-[var(--text-muted)]">
                            ({(citation.similarity * 100).toFixed(0)}% match)
                          </span>
                        )}
                      </div>
                      {citation.description && (
                        <p className="text-[var(--text-muted)] text-xs mt-1 line-clamp-2">
                          {citation.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reasoning - expandable for long analysis with linkified SCG rules */}
          {review.reasoning && (
            <div className="bg-[var(--vault-elevated)] p-3 rounded border border-[var(--vault-border)]">
              <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Analysis</h4>
              <div className="text-sm text-[var(--text-muted)]">
                {review.reasoning.length > ANALYSIS_TRUNCATE_LENGTH && !analysisExpanded ? (
                  <>
                    <div className="whitespace-pre-wrap">
                      {linkifyScgRules(review.reasoning.substring(0, ANALYSIS_TRUNCATE_LENGTH))}...
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setAnalysisExpanded(true); }}
                      className="mt-2 text-[var(--spotlight-gold)] hover:text-[var(--spotlight-gold)] text-xs font-medium"
                    >
                      Show more ▼
                    </button>
                  </>
                ) : (
                  <>
                    <div className="whitespace-pre-wrap">{linkifyScgRules(review.reasoning)}</div>
                    {review.reasoning.length > ANALYSIS_TRUNCATE_LENGTH && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setAnalysisExpanded(false); }}
                        className="mt-2 text-[var(--spotlight-gold)] hover:text-[var(--spotlight-gold)] text-xs font-medium"
                      >
                        Show less ▲
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ClassificationReview() {
  const [textInput, setTextInput] = useState('');
  const [reviewResult, setReviewResult] = useState(null);
  const [activeTab, setActiveTab] = useState('text');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState('all');

  // Text review mutation
  const reviewMutation = useMutation({
    mutationFn: ({ text, options }) => {
      return reviewClassificationMarkings(text, options);
    },
    onMutate: () => {
      setReviewResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(10);
      setLoadingMessage('Extracting portion markings...');

      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 12;
        });
      }, 600);

      window.reviewProgressInterval = progressInterval;

      setTimeout(() => setLoadingMessage('Classifying each portion...'), 1500);
      setTimeout(() => setLoadingMessage('Comparing classifications...'), 3000);
      setTimeout(() => setLoadingMessage('Generating analysis...'), 4500);
    },
    onSuccess: (data) => {
      if (window.reviewProgressInterval) {
        clearInterval(window.reviewProgressInterval);
        window.reviewProgressInterval = null;
      }

      setLoadingProgress(100);
      setTimeout(() => {
        setReviewResult(data);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 1500);
    },
    onError: () => {
      if (window.reviewProgressInterval) {
        clearInterval(window.reviewProgressInterval);
        window.reviewProgressInterval = null;
      }

      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  // File upload mutation
  const uploadMutation = useMutation({
    mutationFn: (file) => {
      return uploadAndReviewClassification(file, {
        useScgRules: true,
        useHyde: true,  // Enable HyDE for better semantic bridging
        useSemanticRetrieval: true
      });
    },
    onMutate: () => {
      setReviewResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(5);
      setLoadingMessage('Uploading document...');

      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 10;
        });
      }, 700);

      window.uploadProgressInterval = progressInterval;

      setTimeout(() => setLoadingMessage('Extracting text from document...'), 1000);
      setTimeout(() => setLoadingMessage('Identifying portion markings...'), 2000);
      setTimeout(() => setLoadingMessage('Analyzing classifications...'), 3500);
    },
    onSuccess: (data) => {
      if (window.uploadProgressInterval) {
        clearInterval(window.uploadProgressInterval);
        window.uploadProgressInterval = null;
      }

      setLoadingProgress(100);
      setTimeout(() => {
        setReviewResult(data);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 1500);
    },
    onError: () => {
      if (window.uploadProgressInterval) {
        clearInterval(window.uploadProgressInterval);
        window.uploadProgressInterval = null;
      }

      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const handleReview = () => {
    if (!textInput.trim()) return;

    reviewMutation.mutate({
      text: textInput,
      options: {
        useScgRules: true,
        useHyde: true,  // Enable HyDE for better semantic bridging
        useSemanticRetrieval: true
      }
    });
  };

  const handleFileUpload = (file) => {
    uploadMutation.mutate(file);
  };

  const isLoading = isLocalLoading || reviewMutation.isPending || uploadMutation.isPending;

  // Filter portions based on severity
  const filteredPortions = reviewResult?.portion_reviews?.filter(review => {
    if (filterSeverity === 'all') return true;
    if (filterSeverity === 'discrepancies') return review.comparison?.has_discrepancy;
    return review.comparison?.severity === filterSeverity;
  }) || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <DocumentMagnifyingGlassIcon className="w-8 h-8 text-[var(--spotlight-gold)]" />
          <h1 className="text-3xl font-bold text-[var(--text-primary)]">Classification Review</h1>
        </div>
        <p className="mt-2 text-[var(--text-muted)]">
          Review pre-marked documents for classification accuracy. Upload a document with existing portion markings
          to identify potential under-classifications or over-classifications.
        </p>
      </div>

      {/* Input Section */}
      <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-6 mb-6">
        {/* Tab selector */}
        <div className="flex border-b border-[var(--vault-border)] mb-4">
          <button
            className={`px-4 py-2 font-medium text-sm ${activeTab === 'text' ? 'text-[var(--spotlight-gold)] border-b-2 border-[var(--spotlight-gold)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
            onClick={() => setActiveTab('text')}
          >
            <DocumentTextIcon className="w-4 h-4 inline mr-2" />
            Paste Text
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${activeTab === 'file' ? 'text-[var(--spotlight-gold)] border-b-2 border-[var(--spotlight-gold)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
            onClick={() => setActiveTab('file')}
          >
            <DocumentMagnifyingGlassIcon className="w-4 h-4 inline mr-2" />
            Upload File
          </button>
        </div>

        {/* Text input tab */}
        {activeTab === 'text' && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Paste portion-marked document text
            </label>
            <textarea
              className="w-full h-64 p-4 border border-[var(--vault-border)] rounded-lg focus:ring-2 focus:ring-[var(--spotlight-gold)] focus:border-[var(--spotlight-gold)] font-mono text-sm bg-[var(--vault-bg)] text-[var(--text-primary)]"
              placeholder={`Paste text with portion markings, for example:

(U) This is unclassified information about general procedures.

(S) This contains secret information about intelligence sources.

(TS//SI//NOFORN) This is top secret signals intelligence that cannot be shared with foreign nationals.

(C) This is confidential information about operations.`}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
            />
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleReview}
                disabled={isLoading || !textInput.trim()}
                className="px-6 py-2 bg-[var(--spotlight-gold)] text-black rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <DocumentMagnifyingGlassIcon className="w-5 h-5 mr-2" />
                Review Classifications
              </button>
            </div>
          </div>
        )}

        {/* File upload tab */}
        {activeTab === 'file' && (
          <div>
            <FileUpload
              onFileSelect={handleFileUpload}
              acceptedTypes=".pdf,.docx,.doc,.txt"
              maxSizeMB={20}
            />
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              Supported formats: PDF, DOCX, DOC, TXT. Max size: 20MB.
            </p>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-8">
          <SecurityShieldLoader
            progress={loadingProgress}
            message={loadingMessage}
          />
        </div>
      )}

      {/* Error State */}
      {(reviewMutation.isError || uploadMutation.isError) && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-400">
              {reviewMutation.error?.message || uploadMutation.error?.message || 'An error occurred during review'}
            </span>
          </div>
        </div>
      )}

      {/* Results Section */}
      {reviewResult && !isLoading && (
        <div className="space-y-6">
          {/* Statistics Overview */}
          <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-6">
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4 flex items-center">
              <ChartBarIcon className="w-6 h-6 mr-2 text-[var(--spotlight-gold)]" />
              Review Summary
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <StatCard
                title="Total Portions"
                value={reviewResult.total_portions}
                icon={DocumentTextIcon}
                color="blue"
              />
              <StatCard
                title="Correct"
                value={reviewResult.correct_portions}
                icon={CheckCircleIcon}
                color="green"
              />
              <StatCard
                title="Discrepancies"
                value={reviewResult.discrepant_portions}
                icon={ExclamationTriangleIcon}
                color={reviewResult.discrepant_portions > 0 ? 'red' : 'green'}
              />
              <StatCard
                title="Under-Classified"
                value={reviewResult.under_classified_count}
                icon={ArrowUpIcon}
                color={reviewResult.under_classified_count > 0 ? 'red' : 'green'}
              />
              <StatCard
                title="Over-Classified"
                value={reviewResult.over_classified_count}
                icon={ArrowDownIcon}
                color={reviewResult.over_classified_count > 0 ? 'yellow' : 'green'}
              />
              <StatCard
                title="Accuracy"
                value={`${reviewResult.overall_accuracy?.toFixed(1)}%`}
                icon={reviewResult.overall_accuracy >= 90 ? ShieldCheckIcon : ShieldExclamationIcon}
                color={reviewResult.overall_accuracy >= 90 ? 'green' : reviewResult.overall_accuracy >= 70 ? 'yellow' : 'red'}
              />
            </div>

            {/* Severity breakdown */}
            {reviewResult.discrepant_portions > 0 && (
              <div className="bg-[var(--vault-elevated)] rounded-lg p-4">
                <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Issues by Severity</h3>
                <div className="flex flex-wrap gap-3">
                  {reviewResult.critical_issues > 0 && (
                    <span className="px-3 py-1 bg-red-900/30 text-red-400 rounded-full text-sm">
                      Critical: {reviewResult.critical_issues}
                    </span>
                  )}
                  {reviewResult.high_issues > 0 && (
                    <span className="px-3 py-1 bg-orange-900/30 text-orange-400 rounded-full text-sm">
                      High: {reviewResult.high_issues}
                    </span>
                  )}
                  {reviewResult.medium_issues > 0 && (
                    <span className="px-3 py-1 bg-yellow-900/30 text-yellow-400 rounded-full text-sm">
                      Medium: {reviewResult.medium_issues}
                    </span>
                  )}
                  {reviewResult.low_issues > 0 && (
                    <span className="px-3 py-1 bg-blue-900/30 text-blue-400 rounded-full text-sm">
                      Low: {reviewResult.low_issues}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Document classification comparison */}
            <div className="mt-4 flex items-center justify-between p-4 bg-[var(--vault-elevated)] rounded-lg">
              <div>
                <p className="text-sm text-[var(--text-muted)]">Document Classification (Current)</p>
                <ClassificationBadge classification={reviewResult.document_classification} />
              </div>
              {reviewResult.document_classification !== reviewResult.suggested_document_classification && (
                <>
                  <ArrowUpIcon className="w-6 h-6 text-[var(--text-muted)]" />
                  <div>
                    <p className="text-sm text-[var(--spotlight-gold)] font-medium">Suggested Classification</p>
                    <ClassificationBadge classification={reviewResult.suggested_document_classification} />
                  </div>
                </>
              )}
              <div className="text-right">
                <p className="text-sm text-[var(--text-muted)]">Processing Time</p>
                <p className="text-lg font-medium text-[var(--text-primary)]">{reviewResult.processing_time?.toFixed(2)}s</p>
              </div>
            </div>
          </div>

          {/* Portion Reviews */}
          <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[var(--text-primary)] flex items-center">
                <DocumentTextIcon className="w-6 h-6 mr-2 text-[var(--spotlight-gold)]" />
                Portion Analysis
              </h2>

              {/* Filter dropdown */}
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="border border-[var(--vault-border)] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--spotlight-gold)] bg-[var(--vault-bg)] text-[var(--text-primary)]"
              >
                <option value="all">All Portions ({reviewResult.total_portions})</option>
                <option value="discrepancies">Discrepancies Only ({reviewResult.discrepant_portions})</option>
                <option value="critical">Critical ({reviewResult.critical_issues})</option>
                <option value="high">High ({reviewResult.high_issues})</option>
                <option value="medium">Medium ({reviewResult.medium_issues})</option>
                <option value="low">Low ({reviewResult.low_issues})</option>
              </select>
            </div>

            {/* Portion list */}
            <div className="space-y-3">
              {filteredPortions.length === 0 ? (
                <div className="text-center py-8 text-[var(--text-muted)]">
                  <CheckCircleIcon className="w-12 h-12 mx-auto mb-2 text-green-400" />
                  <p>No portions match the selected filter.</p>
                </div>
              ) : (
                filteredPortions.map((review, index) => (
                  <PortionReviewCard key={index} review={review} index={index} />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
